import { useForm as createUseForm } from "@ant-design-vue/use";
import { Col, Row } from "ant-design-vue";
import lodash from "lodash";
import { defineAsyncComponent, reactive, toRaw } from "vue";
import { setIsLoading as setIsLoadingApp } from "../../../app.logic";
import { setAppPageHeader } from "../../../components/layout/app-layout/app-layout.logic";
import i18n from "../../../i18n";
import store from "../../../store";
import { createButton, createButtonCreate, createButtonEdit, createButtonSave, createButtonView } from "../../../utils/button";
import { checkAttribute, initUseForm, onClickCreate, onClickEdit, onClickView, watchRulesChildren } from "../../../utils/form";
import { createLoading } from "../../../utils/loading";
import { has } from "../../../utils/menu-context";
import {
    createAppPageHeaderTop,
    createManageExportCsv,
    createManageExtraCreate,
    createManageSelect,
    createPage,
    createRowActionEdit,
    createRowActionView
} from "../../../utils/page";
import { flatWhereGroup } from "../../../utils/search";
import { required, requiredWhen, validateError } from "../../../utils/validate";
import websocket from "../../../utils/websocket";
import { createForm as createFormOfficer, createRules as createRulesOfficer } from "../../account/register-officer/register-officer.logic";
import { mapLinkageGetData } from "../company/mgt-tab/tab-1/tab1.logic";
import { callUserRequestStatus } from "../company/mgt-tab/tab-4/tab4.section";
import { openModalForm as openModalFormConfirmWaitApproveCenter } from "./modal/confirm-wait-approve-center.logic";
import { createConditionAction, createConditionForm } from "./officer-approve.condition";
import { createColumns, createField, onLinkIdCard } from "./officer-approve.section";

const CIcon = defineAsyncComponent(() => import("../../../components/icon/CIcon"));

export const { isLoading, setIsLoading } = createLoading();

export const actionPrefix = "resource/v1/transaction/userRequest";
export const routerPrefix = "officer-approves";

export const callRequest = ({ form }) => store.dispatch(`${actionPrefix}/request`, { data: form });
export const callSearch = ({ criteria }) => store.dispatch(`${actionPrefix}/search`, { criteria });
export const callRequestApproveOfficer = ({ id }) => store.dispatch(`${actionPrefix}/requestApproveOfficer`, { id });
export const callRequestNotApproveOfficer = ({ data }) => store.dispatch(`${actionPrefix}/requestNotApproveOfficer`, { data });

export const onSearch = ({ props, page }) => {
    const where = page.where || [];
    const sort = page.sort || [];
    setIsLoading(true);
    window.onSearch?.({ page });
    return callSearch({ criteria: { ...page, data: [], where: [...where, ...createWhereFor({ props })], sort } })
        .then(page => (state.page = { ...page, where, sort }))
        .finally(() => setIsLoading(false));
};

export const onClickSave = ({ props, data, refs, formParams }) => {
    const { viewOnly } = props;
    return data.useForm
        .validate()
        .then(() => {
            setIsLoadingApp(true);
            return callRequest({ form: { ...data.useForm.modelRef, ...formParams } })
                .finally(() => setIsLoadingApp(false))
                .then(record => {
                    if (viewOnly) initForm({ props, data, refs });
                    else onClickView({ record, routerPrefix });
                });
        })
        .catch(error => {
            validateError({ error });
            throw error;
        });
};

export const onClickRequest = ({ props, data, refs, callRequest }) => {
    const { viewOnly } = props;
    setIsLoadingApp(true);
    return callRequest()
        .finally(() => setIsLoadingApp(false))
        .then(record => {
            if (viewOnly) initForm({ props, data, refs });
            else onClickView({ record, routerPrefix });
        });
};

export const onClickModalOk = ({ data }) => {
    return data.useForm
        .validate()
        .then(() => ({ ...toRaw(data.useForm.modelRef), isSave: true }))
        .catch(error => {
            validateError({ error });
            throw error;
        });
};

export const createSearchOneCriteria = ({ id, forLog }) => {
    if (!forLog) {
        return {
            select: ["*", "refUserTemp.**"],
            page: 0,
            size: 1,
            where: [{ name: "id", type: "UUID", operator: "=", value: id }],
            children: [
                { name: "refUserTemp.attachments", select: ["*"], page: 0, size: 1000000, sort: [{ name: "createDate", type: "ASC" }] },
                {
                    name: "statuses",
                    asName: "lastStatus.forRequestRemark",
                    select: ["**"],
                    page: 0,
                    size: 1,
                    where: [{ name: "refStatus.key", type: "String", operator: "in", value: ["draft", "reEdit"] }],
                    sort: [{ name: "createDate", type: "DESC" }]
                },
                {
                    name: "statuses",
                    asName: "lastStatus.forOfficeRemark",
                    select: ["**"],
                    page: 0,
                    size: 1,
                    where: [
                        {
                            name: "refStatus.key",
                            type: "String",
                            operator: "in",
                            value: ["waitReceiveCustoms", "waitApproveCenter", "waitDocCustoms"]
                        }
                    ],
                    sort: [{ name: "createDate", type: "DESC" }]
                }
            ]
        };
    } else {
        return {
            select: [],
            page: 0,
            size: 1,
            where: [{ name: "id", type: "UUID", operator: "=", value: id }],
            children: []
        };
    }
};

export const createForm = ({ props, data, refs }) => {
    return {
        model: null,
        refModelId: null,
        refModelTempId: null,
        refRequestType: null,
        refRequestDoc: null,
        refStatus: null,
        refDepartment: null,
        statusRemark: null,
        refUser: null,
        refUserTemp: { ...createFormOfficer({ props, data, refs }) },
        statuses: [],
        lastStatus: {
            forRequestRemark: [{ remark: null, statusDatetime: null }],
            forOfficeRemark: [{ remark: null, statusDatetime: null }]
        }
    };
};

export const createRules = ({ props, data, refs }) => {
    return {
        ...createRulesOfficer({ props, data, refs, key: "refUserTemp" })
    };
};

export const createRulesAttachment = ({ data, key }) => {
    const modelRef = data.useForm?.modelRef || data.form;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    const rules = {};
    model?.refUserTemp?.attachments?.forEach((item, index) => {
        if (item.isDelete !== true) {
            rules[`${prefix}refUserTemp.attachments.${index}.documentName`] = [required];
            rules[`${prefix}refUserTemp.attachments.${index}.size`] = [
                requiredWhen({ when: () => model.refUserTemp.attachments[index].attribute?.isRequiredAttachment })
            ];
        }
    });
    return rules;
};

export const createFormTitle = ({ data }) => {
    const modelRef = data.useForm.modelRef;
    return modelRef.id ? `${modelRef.refUserTemp?.fullNameTh} (${modelRef.refStatus?.value})` : i18n.global.t("button.create");
};

export const createWhereFor = () => {
    const whereGroup = [];
    return [{ name: "refRequestDoc.key", type: "String", operator: "=", value: "7" }, ...flatWhereGroup({ where: whereGroup, logic: "OR" })];
};

export const loadForm = ({ props, data, refs, state, companyId }) => {
    setIsLoading(true);
    const { form, routerPrefix } = props;
    const { id, useForm } = data;
    const { modelRef } = useForm;
    let promise = null;
    if (form) {
        promise = Promise.resolve({ data: [form] });
    } else if (id) {
        promise = callSearch({ criteria: createSearchOneCriteria({ props, data, refs, state, companyId, id }) });
    } else {
        promise = Promise.resolve({ data: [useForm.modelRef] });
    }
    return promise
        .then(page => initUseForm({ props, data, refs, page, routerPrefix }))
        .then(() => loadDefaultForm({ props, data, refs, routerPrefix }))
        .then(() => initUseForm({ props, data, refs, page: { data: [modelRef] }, routerPrefix }))
        .finally(() => setIsLoading(false));
};

export const loadDefaultForm = ({ data }) => {
    const promises = [callUserRequestStatus({})];
    return Promise.all(promises).then(arr => {
        data.statuses = arr[0];
    });
};

export const initState = ({ props }) => {
    const menuContext = props.menuContext;
    state.manages = [createManageSelect({ menuContext }), createManageExportCsv({ menuContext })];
    state.manageExtras = [createManageExtraCreate({ menuContext, onClick: () => onClickCreate({ routerPrefix }) })];
    state.rowActions = [
        createRowActionView({ menuContext, onClick: ({ record }) => onClickView({ record, routerPrefix }) }),
        createRowActionEdit({
            menuContext,
            onClick: ({ record }) => onClickEdit({ record, routerPrefix }),
            visible: ({ record }) => state.conditionAction.canEdit({ props, record })
        })
    ];
};

export const initForm = ({ props, data, refs }) => {
    checkAttribute({ props, data, refs });
    data.form = createForm({ props, data, refs });
    data.rules = createRules({ props, data, refs });
    data.useForm = createUseForm(data.form, data.rules);
    data.fields = createField({ props, data, refs, isLoading });
    data.conditionForms = createConditionForm({ props, data, refs, isLoading });
    watchRulesChildren({
        props,
        data,
        refs,
        trigger: () => data.useForm.modelRef.refUserTemp.attachments.filter(d => d.isDelete !== true).length,
        createRules: () => createRulesAttachment({ props, data, refs }),
        itemsKey: "refUserTemp.attachments"
    });
    loadForm({ props, data, refs }).then(() => {
        if (props.setAppPageHeader) {
            setAppPageHeader({
                slots: {
                    top: () => createAppPageHeaderTop({ props, data, refs, state, routerPrefix, onClickView, onClickEdit }),
                    default: () => createAppPageHeaderDefault({ props, data, refs })
                }
            });
        }
    });
};

export const createAppPageHeaderDefault = ({ props, data, refs }) => {
    const col1 = [];
    const col2 = [];
    const { menuContext, viewOnly } = props;
    const { useForm } = data;
    const { modelRef, initialModel } = useForm;
    const { id, isActive } = modelRef;
    const canEdit = state.conditionAction.canEdit({ props, record: initialModel });
    const canApprove = state.conditionAction.canApprove({ props, record: initialModel });
    if (id && has({ menuContext, key: "canCreate" })) {
        col1.push(createButtonCreate({ onClick: () => onClickCreate({ routerPrefix }), classStyle: "m-1" }));
    }
    if (!viewOnly && id && has({ menuContext, key: "canView" })) {
        col1.push(createButtonView({ onClick: () => onClickView({ record: { id }, routerPrefix }), classStyle: "m-1" }));
    }
    if (viewOnly && id && isActive && canEdit) {
        col1.push(createButtonEdit({ onClick: () => onClickEdit({ record: { id }, routerPrefix }), classStyle: "m-1" }));
    }
    if (!viewOnly && (!id || (id && isActive && canEdit))) {
        col1.push(
            createButtonSave({
                onClick: () => {
                    let statusRemark = null;
                    if ([undefined, "draft", "reEdit"].includes(initialModel.refStatus?.key)) {
                        statusRemark = modelRef.lastStatus.forRequestRemark[0].remark;
                    } else if (["waitReceiveCustoms", "waitApproveCenter", "waitDocCustoms"].includes(initialModel.refStatus?.key)) {
                        statusRemark = modelRef.lastStatus.forOfficeRemark[0].remark;
                    }
                    onClickSave({
                        props,
                        data,
                        refs,
                        formParams: { statusRemark }
                    });
                },
                classStyle: "m-1"
            })
        );
    }
    if (id && isActive && canApprove && viewOnly) {
        //const statusApprove = data.statuses.find(status => status.key === "approve");
        col1.push(
            createButton({
                text: `${viewOnly ? "" : `${i18n.global.t("button.save")} & `}${i18n.global.t("button.sendRequest")}`,
                classStyle: `m-1 btn-primary`,
                onClick: () => {
                    openModalFormConfirmWaitApproveCenter({
                        onOk: res => {
                            let statusRemark = null;
                            if ([undefined, "draft", "reEdit"].includes(initialModel.refStatus?.key)) {
                                statusRemark = modelRef.lastStatus.forRequestRemark[0].remark;
                            } else if (["waitReceiveCustoms", "waitApproveCenter", "waitDocCustoms"].includes(initialModel.refStatus?.key)) {
                                statusRemark = modelRef.lastStatus.forOfficeRemark[0].remark;
                            }
                            if (res.refStatus.key === "approve") {
                                onClickRequest({ props, data, refs, callRequest: () => callRequestApproveOfficer({ id }) });
                            } else {
                                onClickRequest({
                                    props,
                                    data,
                                    refs,
                                    callRequest: () => callRequestNotApproveOfficer({ data: { ...modelRef, statusRemark } })
                                });
                            }
                        }
                    });
                },
                icon: viewOnly ? null : <CIcon file="save.svg"></CIcon>
            })
        );
    }
    return (
        <Row type="flex">
            <Col>{col1}</Col>
            <Col class="ml-auto">{col2}</Col>
        </Row>
    );
};

export const onWebsocketConnect = ({ props, data }) => {
    state.websocketSubscribes.push(
        websocket.subscribe(`/user/topic/linkage/get/data`, res => {
            mapLinkageGetData({ data, key: "refUserTemp", linkageRes: res });
            if (data.linkageReceive001 && data.linkageReceive009) {
                onLinkIdCard({ props, data, isLoading })(data.linkIdCard);
            }
            setIsLoadingApp(false);
        })
    );
};

export const state = reactive({
    columns: createColumns(),
    page: createPage({
        options: {
            select: ["*", "refRequestType.*", "refStatus.*", "refUserTemp.**"],
            sort: [
                /* { name: "refStatus.seq", type: "ASC" } ,*/
                { name: "updateDate", type: "DESC" }
            ],
            children: [
                {
                    name: "statuses",
                    asName: "lastStatus.approve",
                    select: ["**"],
                    page: 0,
                    size: 1,
                    where: [
                        {
                            name: "refStatus.key",
                            type: "String",
                            operator: "=",
                            value: "approve"
                        }
                    ],
                    sort: [{ name: "createDate", type: "DESC" }]
                }
            ]
        }
    }),
    tableOptions: { search: { activeKey: "Search" } },
    conditionAction: createConditionAction(),
    websocketId: null,
    websocketSubscribes: []
});
